import {
  Article,
  ArticleImage,
  ArticleList,
  ArticleSubtitle,
  ArticleTitle,
} from "@babackapp/customer-ui/components/article";
import { SectionHeader } from "@babackapp/customer-ui/components/header";
import {
  ReceiptLine,
  ReceiptLineLabel,
  ReceiptLineValue,
} from "@babackapp/customer-ui/components/receipt-line";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { formatPrice } from "@babackapp/return-widget-components/utils/format-price";
import type { SearchReturnRequestsResponseDto } from "@babackapp/private-api-client";
import { Separator } from "@babackapp/ui/components/separator";
import { useLineItemImages } from "../../hooks/use-line-item-images";
import { ReturnRequestLineItem } from "./return-request-line-item";

type ReturnRequest = SearchReturnRequestsResponseDto["returnRequests"][number];

type Props = {
  returnRequest: ReturnRequest;
};

export function ReturnItemsSummary({ returnRequest }: Props) {
  const t = useCustomText();
  const lineItems = useLineItemImages(returnRequest);

  const exchangeItems = lineItems.filter(
    (li) => li.resolution === "exchange" || li.resolution === "exchange-credit" || li.resolution === "new-articles",
  );
  const refundItems = lineItems.filter((li) => li.resolution === "refund");
  const discountItems = lineItems.filter((li) => li.resolution === "discount");

  const fi = returnRequest.financial_informations;
  const currency = fi.refund.currency || returnRequest.currency.shopCurrency;
  const refundAmount = fi.refund.presentmentAmount;
  const discountAmount = fi.discount.presentmentAmount;
  const rebuyAmount = fi.rebuy.presentmentAmount;
  const rebuyCurrency = fi.rebuy.currency || currency;
  const exchangeCreditAmount = fi.exchangeCredit?.presentmentAmount ?? 0;
  const rebuyItems = returnRequest.exchange?.newProductForExchangeCredit ?? [];

  const hasFinancials = refundAmount > 0 || discountAmount > 0 || rebuyAmount > 0 || exchangeCreditAmount > 0;

  return (
    <div className="flex flex-col gap-6 w-full">
      {exchangeItems.length > 0 && (
        <div className="flex flex-col gap-2">
          <SectionHeader>{t("summary.exchangeItems")}</SectionHeader>
          <ArticleList size="sm" variants={{}}>
            {exchangeItems.map((li) => (
              <ReturnRequestLineItem
                key={li.line_item_id}
                lineItem={li}
                currency={currency}
                imageLayoutId={`${returnRequest.id}${li.line_item_id}${li.image}`}
              />
            ))}
          </ArticleList>
        </div>
      )}

      {refundItems.length > 0 && (
        <div className="flex flex-col gap-2">
          <SectionHeader>{t("summary.refundItems")}</SectionHeader>
          <ArticleList size="sm" variants={{}}>
            {refundItems.map((li) => (
              <ReturnRequestLineItem
                key={li.line_item_id}
                lineItem={li}
                currency={currency}
                imageLayoutId={`${returnRequest.id}${li.line_item_id}${li.image}`}
              />
            ))}
          </ArticleList>
        </div>
      )}

      {discountItems.length > 0 && (
        <div className="flex flex-col gap-2">
          <SectionHeader>{t("summary.discountItems")}</SectionHeader>
          <ArticleList size="sm" variants={{}}>
            {discountItems.map((li) => (
              <ReturnRequestLineItem
                key={li.line_item_id}
                lineItem={li}
                currency={currency}
                imageLayoutId={`${returnRequest.id}${li.line_item_id}${li.image}`}
              />
            ))}
          </ArticleList>
        </div>
      )}

      {rebuyItems.length > 0 && (
        <div className="flex flex-col gap-2">
          <SectionHeader>{t("completeSummary.newItems")}</SectionHeader>
          <ArticleList size="sm" variants={{}}>
            {rebuyItems.map((item) => (
              <Article key={item.id} variants={{}}>
                {item.image && <ArticleImage src={item.image} />}
                <ArticleTitle>{item.title ?? ""}</ArticleTitle>
                <ArticleSubtitle>
                  ×{item.quantity} — {formatPrice(item.price * item.quantity, rebuyCurrency)}
                </ArticleSubtitle>
              </Article>
            ))}
          </ArticleList>
        </div>
      )}

      {hasFinancials && (
        <>
          <Separator />
          <div className="flex flex-col gap-3">
            {refundAmount > 0 && (
              <ReceiptLine>
                <ReceiptLineLabel>{t("financial.refundTotal")}</ReceiptLineLabel>
                <ReceiptLineValue>{formatPrice(refundAmount * 100, currency)}</ReceiptLineValue>
              </ReceiptLine>
            )}
            {discountAmount > 0 && (
              <ReceiptLine>
                <ReceiptLineLabel>{t("financial.discount")}</ReceiptLineLabel>
                <ReceiptLineValue>{formatPrice(discountAmount * 100, currency)}</ReceiptLineValue>
              </ReceiptLine>
            )}
            {exchangeCreditAmount > 0 && (
              <ReceiptLine>
                <ReceiptLineLabel>{t("financial.exchangeCredit")}</ReceiptLineLabel>
                <ReceiptLineValue>{formatPrice(exchangeCreditAmount * 100, currency)}</ReceiptLineValue>
              </ReceiptLine>
            )}
            {rebuyAmount > 0 && (
              <ReceiptLine>
                <ReceiptLineLabel>{t("completeSummary.newItemsLabel")}</ReceiptLineLabel>
                <ReceiptLineValue>−{formatPrice(rebuyAmount * 100, rebuyCurrency)}</ReceiptLineValue>
              </ReceiptLine>
            )}
            {exchangeCreditAmount > 0 && rebuyAmount > 0 && (() => {
              const net = exchangeCreditAmount - rebuyAmount;
              return (
                <>
                  <Separator />
                  <ReceiptLine>
                    <ReceiptLineLabel className="font-semibold text-foreground">
                      {net >= 0 ? t("completeSummary.netRefund") : t("completeSummary.remainingToPay")}
                    </ReceiptLineLabel>
                    <ReceiptLineValue className="font-semibold text-foreground">
                      {formatPrice(Math.abs(net) * 100, currency)}
                    </ReceiptLineValue>
                  </ReceiptLine>
                </>
              );
            })()}
          </div>
        </>
      )}
    </div>
  );
}
